import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TablePagination,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  IconButton,
  Checkbox,
} from "@mui/material";
import { AccountBoxOutlined, Add, Cancel, CheckCircle, OpenInNew, Search } from "@mui/icons-material";
import { ErrorModal } from "../modals";
import { RequestError, useApi } from "../../services";
import { User, useUserContext } from "../../context";
import { ChangeUserPassBtn } from "./changePassBtn";
import { FuncionarioRegister } from "../funcionarios/funcionariosRegister";

interface UsersTableProps {
  title?: string;
  selfFirst?: boolean;
  isSearchUser?: boolean;
  isAddUser?: boolean;
  onSearch?: (userId: string) => void;
}
export const HbrUsersTable: React.FC<UsersTableProps> = ({ onSearch, title, isAddUser, selfFirst, isSearchUser }) => {
  const api = useApi();
  const user = useUserContext();

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<RequestError | undefined>(undefined);

  // Dados da tabela de usuários
  const lastTime = useRef<NodeJS.Timeout>();
  const [name, setName] = useState<string>();
  const [state, setState] = useState(false);
  const [users, setUsers] = useState<string[] | RequestError>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(50);
  const [page, setPage] = useState<number>(1);

  // Lista de Usuários
  useEffect(() => {
    const getUsers = async () => {
      if (!user || "error" in user) return;

      let data;
      if (name && isSearchUser) {
        data = await api.user.getByName(name, page, perPage);
      }
      else {
        data = await api.user.getAllInMyCompany(page, perPage);
      }

      if ("error" in data) {
        setUsers({
          error: data.error,
          message: data.message,
        });
        return;
      }

      setTotalItems(data.totalItems);
      // Organizando o próprio usuário antes
      if (selfFirst === true || selfFirst === undefined) {
        const idIndex = data.items.indexOf(user.id);
        if (idIndex >= 0) data.items.splice(idIndex, 1);
        data.items.unshift(user.id);
      }
      setUsers(data.items);
    };
    getUsers();
  }, [page, perPage, user, state, name]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleCloseAlert = () => setError(undefined);

  const handleAddUser = () => {
    setOpen(false);
    setState(!state);
  };

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (lastTime.current) clearTimeout(lastTime.current);
    lastTime.current = setTimeout(() => setName(event.target.value), 500);
  };

  return (
    <>
      {/* Erro ao adicionar um novo usuário */}
      {error &&
        <Snackbar
          open={error !== null}
          autoHideDuration={4500}
          onClose={handleCloseAlert}
          message="Note archived"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={handleCloseAlert} severity="error" sx={{ width: "100%" }}>
            {error.error + ":" + error.message}
          </Alert>
        </Snackbar>
      }

      {/* Modal do new user */}
      {isAddUser && user && !("error" in user) &&
        < FuncionarioRegister
          open={open}
          sendPassword
          userEmpreendimento={user.empreendimento}
          onAdd={handleAddUser}
          onClose={handleClose}
        />
      }

      {/* Título */}
      < Box sx={{
        display: "flex",
        flexDirection: "row",
        height: "2.3em",
      }}>
        <Typography fontWeight={"bold"} fontSize={24}>
          {title ?? "Usuários"}
        </Typography>

        {isAddUser && user && !("error" in user) && user.admin &&
          <>
            <div style={{ flex: 1 }} />
            {/* Botão para adicionar novo usuário */}
            <Button
              startIcon={<Add />}
              endIcon={<AccountBoxOutlined />}
              variant="contained"
              onClick={handleOpen}>
              Adicionar Usuário
            </Button>
          </>
        }
      </Box >

      {/* Escolher por nome */}
      {isSearchUser &&
        <Box sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          height: "3em",
        }}>
          <TextField
            fullWidth
            label="Nome do Usuário"
            variant="standard"
            onChange={handleChangeName}
          />
          <Search />
        </Box>
      }

      {/* Tabela de Usuários */}
      {"error" in users && <ErrorModal {...users} />}

      {!("error" in users) &&
        <>
          <TableContainer sx={{ height: `calc(100vh - 2.3em${isSearchUser ? ' - 3em' : ''} - 52px - 95px)` }}>
            <Table stickyHeader sx={{ minWidth: 750 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {onSearch && <TableCell align="center" sx={{ fontWeight: "bold" }}>Selecionar</TableCell>}
                  <TableCell sx={{ fontWeight: "bold" }}>Nome</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>Email</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>Empresa</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>Último Acesso</TableCell>
                  {user && !("error" in user) && user.admin && <TableCell align="center" sx={{ fontWeight: "bold" }}>Admin</TableCell>}
                  {isAddUser && <TableCell align="center" sx={{ fontWeight: "bold" }}>Alterar Senha</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(id => <RowUser
                  key={id}
                  id={id}
                  onSearch={onSearch}
                  isAddUser={isAddUser}
                />)}
              </TableBody>
            </Table>
          </TableContainer >

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            sx={{ height: "52px" }}
            count={totalItems}
            rowsPerPage={perPage}
            page={page - 1}
            onPageChange={(ev, page) => setPage(page + 1)}
            onRowsPerPageChange={(ev) => setPerPage(parseInt(ev.target.value, 10))}
          />
        </>
      }
    </>
  );
};

const RowUser: React.FC<{
  id: string;
  isAddUser?: boolean;
  onSearch?: (userId: string) => void;
}> = ({ id, isAddUser, onSearch }) => {
  const api = useApi();
  const user = useUserContext();

  const [userData, setUserData] = useState<User | RequestError>();


  useEffect(() => {
    const getUser = async () => {
      const user = await api.user.getUserById(id);

      if ("error" in user) {
        setUserData(user);
        return;
      }

      const empresa = await api.company.company(user.empreendimento);
      if ("error" in empresa) {
        setUserData(empresa);
        return;
      }

      setUserData({
        ...user,
        empresa: empresa,
      });
    };

    getUser();
  }, []);

  const handleAdmin = async (isAdm: boolean) => {
    if (!userData || "error" in userData) return;

    await api.user.update(userData.id, { admin: isAdm }).then(async (value) => {
      if ("error" in value) {
        setUserData(value);
        return;
      }

      const empresa = await api.company.company(value.empreendimento);
      if ("error" in empresa) {
        setUserData(empresa);
        return;
      }

      setUserData({
        ...value,
        empresa: empresa,
      });
    });
  };


  let setAdm = <TableCell align="center"></TableCell>;
  if (isAddUser && user && !("error" in user) && user.admin && userData && !("error" in userData)) {
    if (user.id !== id) {
      setAdm = <TableCell align="center">
        <Checkbox
          checked={userData.admin}
          onChange={(ev, checked) => handleAdmin(checked)}
        />
      </TableCell>;
    } else {
      setAdm = <TableCell align="center">
        {userData.admin ? <CheckCircle /> : <Cancel />}
      </TableCell>;
    }
  }

  let changePassItem = <TableCell align="center"></TableCell>;
  if (isAddUser && user && !("error" in user) && id == user.id) {
    changePassItem = <TableCell align="center">
      <ChangeUserPassBtn />
    </TableCell>;
  }

  if (!userData) return <TableRow>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    <TableCell align="center"><CircularProgress size={20} /></TableCell>
    {changePassItem}
  </TableRow>;

  if ("error" in userData) return <TableRow>
    <TableCell>Erro</TableCell>
    <TableCell>{userData.message}</TableCell>
  </TableRow>;

  return <TableRow>
    {onSearch && <TableCell align="center">
      <IconButton onClick={() => onSearch(id)}>
        <OpenInNew />
      </IconButton>
    </TableCell>}
    <TableCell>{userData.nome}</TableCell>
    <TableCell align="center">{userData.email}</TableCell>
    <TableCell align="center">{userData?.empresa?.nome ?? "-"}</TableCell>
    <TableCell align="center">{userData.updateDate?.toLocaleString() ?? "-"}</TableCell>
    {user && !("error" in user) && user.admin && setAdm}
    {changePassItem}
  </TableRow>;
};
